import React, { KeyboardEvent } from 'react';
import { getNextFocusableElement } from '../../../../../../_helpers/dom';
import { getKeys } from '../../../../../../_helpers/keyboard';
import HeaderMenuItem from '../header-menu-item/HeaderMenuItem';
import { Menu } from '../../../../../../components/blackrock/advisor-center/_types/navigation';
import { Cta } from '../../../../../../components/blackrock/advisor-center/cta/Cta.tsx';

import './HeaderSecondaryNavigation.scss';

export type HeaderSecondaryNavigationProps = {
	isMobile: boolean;
	isExpanded: boolean;
	setExpanded: (state: boolean) => void;
	menu: Menu[];
	primaryNavItemIndex: number;
	setPrimaryNavItemIndex: React.Dispatch<number>;
	header: React.MutableRefObject<HTMLDivElement>;
	secondaryNavItemIndex: number;
	setSecondaryNavItemIndex: React.Dispatch<number>;
};

const HeaderSecondaryNavigation: React.FC<HeaderSecondaryNavigationProps> = ({
	isMobile,
	isExpanded,
	setExpanded,
	menu,
	primaryNavItemIndex,
	setPrimaryNavItemIndex,
	header,
	secondaryNavItemIndex,
	setSecondaryNavItemIndex,
}) => {
	const classNameValue = isExpanded ? 'is-expanded' : '';

	const onMouseEnter = () => {
		if (!isMobile) {
			setExpanded(true);
		}
	};

	const onMouseLeave = () => {
		if (!isMobile) {
			setExpanded(false);
		}
	};

	const onClick = (): void => {
		setExpanded(false);
	};

	const onKeyDown = (event: KeyboardEvent): void => {
		const secondaryContainer = header.current.querySelector(
			'.blk-ac-header-secondary-navigation'
		);
		const secondaryVisibleWrapper =
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			secondaryContainer.querySelectorAll('.wrapper')[primaryNavItemIndex];
		const secondaryVisibleLinks =
			secondaryVisibleWrapper.querySelectorAll('.secondary a');

		const targetIsFirst = secondaryNavItemIndex === 0;
		const targetIsLast =
			secondaryNavItemIndex === secondaryVisibleLinks.length - 1;
		const keys = getKeys(event.nativeEvent);

		const primaryContainer = header.current.querySelector(
			'.blk-ac-header-primary-navigation'
		);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const primaryVisibleLinks = primaryContainer.querySelectorAll(
			'.primary:not(.blk-ac-only-mobile) a'
		);
		const primaryIsLast =
			primaryVisibleLinks.length - 1 === primaryNavItemIndex;

		if (keys.isDown) {
			event.stopPropagation();
			event.preventDefault();
		}

		if (!targetIsLast && (keys.isDown || keys.isTab)) {
			event.stopPropagation();
			event.preventDefault();

			const newSecondaryNavItemIndex = secondaryNavItemIndex + 1;
			const link = secondaryVisibleLinks[
				newSecondaryNavItemIndex
			] as HTMLElement;

			link.focus();
			setSecondaryNavItemIndex(newSecondaryNavItemIndex);
		}

		if (!targetIsFirst && (keys.isUp || keys.isShiftTab)) {
			event.stopPropagation();
			event.preventDefault();

			const newSecondaryNavItemIndex = secondaryNavItemIndex - 1;
			const link = secondaryVisibleLinks[
				newSecondaryNavItemIndex
			] as HTMLElement;

			link.focus();
			setSecondaryNavItemIndex(newSecondaryNavItemIndex);
		}

		if (targetIsLast && primaryIsLast && keys.isTab) {
			event.stopPropagation();
			event.preventDefault();

			const target = event.target as HTMLElement;
			const body = window.document.body as HTMLElement;
			const nextElement = getNextFocusableElement(target, body);

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			nextElement.focus();
			setExpanded(false);

			return;
		}

		if (targetIsLast && !primaryIsLast && (keys.isDown || keys.isTab)) {
			event.stopPropagation();
			event.preventDefault();

			const newPrimaryNavItemIndex = primaryNavItemIndex + 1;
			const primaryLink = primaryVisibleLinks[
				newPrimaryNavItemIndex
			] as HTMLElement;

			primaryLink.focus();
			setExpanded(false);
			setPrimaryNavItemIndex(newPrimaryNavItemIndex);
		}

		if (targetIsFirst && (keys.isUp || keys.isShiftTab)) {
			event.stopPropagation();
			event.preventDefault();

			const primaryLink = primaryVisibleLinks[
				primaryNavItemIndex
			] as HTMLElement;

			primaryLink.focus();
			setExpanded(false);
		}

		if (keys.isEscape) {
			const primaryLink = primaryVisibleLinks[
				primaryNavItemIndex
			] as HTMLElement;

			primaryLink.focus();
			setExpanded(false);
		}
	};

	return (
		<div
			className={[
				'blk-ac-header-secondary-navigation behind-bg',
				classNameValue,
			].join(' ')}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="background-wrapper">
				{!!menu.length &&
					menu.map(
						(
							{ menuGroups, backLabel, name, hideOnDesktop, spotlight },
							key
						) => {
							const visibleClass = key === primaryNavItemIndex ? 'visible' : '';

							return (
								<div className={['wrapper', visibleClass].join(' ')} key={key}>
									<div
										className="secondary-navigation-close"
										onClick={() => onClick()}
									>
										<i className="fa fa-chevron-left secondary-navigation-close-icon" />
										<span className="secondary-navigation-close-label blk-ac-body-m-bold">
											{backLabel || name}
										</span>
									</div>
									{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
									// @ts-ignore */}
									{menuGroups.map(({ name, menuItems, cta }) => {
										return (
											<div key={name} className={'secondary-navigation-group'}>
												<div>
													{name && (
														<h3 className="secondary-navigation-group-title blk-ac-body-s">
															{name}
														</h3>
													)}
													<ul className="secondary-navigation-group-items">
														{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
														// @ts-ignore */}
														{menuItems.map((item) => (
															<li key={JSON.stringify(item)}>
																<HeaderMenuItem
																	className="secondary blk-ac-body-m"
																	onKeydown={onKeyDown}
																	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
																	// @ts-ignore
																	label={item.displayValue}
																	href={item.href}
																	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
																	// @ts-ignore
																	target={item.target}
																	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
																	// @ts-ignore
																	ariaLabel={item.ariaLabel}
																	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
																	// @ts-ignore
																	dataLinkEvent={item.dataLinkEvent}
																	hasChild={false}
																	titleValue=""
																	onClick={() => onClick()}
																	tabIndex={
																		!isExpanded || hideOnDesktop ? -1 : 0
																	}
																></HeaderMenuItem>
															</li>
														))}
														{cta?.label && cta?.url && (
															<li className="secondary">
																<Cta
																	onKeyDown={onKeyDown}
																	url={cta.url}
																	target={cta.target}
																	ariaLabel={cta.ariaLabel}
																	dataLinkEvent={cta.dataLinkEvent}
																	label={cta.label}
																/>
															</li>
														)}
													</ul>
												</div>
											</div>
										);
									})}
									{spotlight && (
										<div className={'secondary-navigation-group spotlight'}>
											<div
												className="spotlight-content"
												onClick={(e) => {
													if ((e.target as HTMLElement).tagName === 'A') {
														e.preventDefault();
													}
													window.open(
														spotlight.callToAction.url,
														spotlight.callToAction.target
													);
												}}
											>
												<h3 className="secondary-navigation-group-title blk-ac-body-s spotlight">
													{spotlight.title}
												</h3>
												<div className="secondary-navigation-group-items">
													<h4 className="blk-ac-heading-xxs">
														{spotlight.header}
													</h4>
													<p className="blk-ac-body-m">{spotlight.text}</p>
												</div>
												{spotlight.callToAction?.label &&
													spotlight.callToAction?.url && (
														<ul className="secondary-navigation-group-items">
															<li className="secondary">
																<Cta
																	onKeyDown={onKeyDown}
																	url={spotlight.callToAction.url}
																	target={spotlight.callToAction.target}
																	ariaLabel={spotlight.callToAction.ariaLabel}
																	dataLinkEvent={
																		spotlight.callToAction.dataLinkEvent
																	}
																	label={spotlight.callToAction.label}
																/>
															</li>
														</ul>
													)}
											</div>
										</div>
									)}
								</div>
							);
						}
					)}
			</div>
		</div>
	);
};

export default HeaderSecondaryNavigation;
